import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, BehaviorSubject } from 'rxjs';

import { doc, docData, collection, collectionData, Firestore, query, orderBy, where, addDoc, updateDoc, deleteDoc, setDoc, deleteField, getDocs, collectionGroup, writeBatch, startAfter } from '@angular/fire/firestore';

import { environment } from '../../environments/environment';

import { TranslateService } from '@ngx-translate/core';

import { map, take, first } from 'rxjs/operators';

import { Game, Picture } from '../classes/index';

import { v4 as uuid } from 'uuid';
import { ThemeService } from './theme.service';
import { LanguageService } from './language.service';

@Injectable()
export class GameService {

	games;

	private _games: BehaviorSubject<Game[]>;
  	public gameList: Observable<Game[]>;

	totalPlayerCount;
	teamCount;
	teamCountInitiated;
	teamCountInitiatedAndCompleted;

	constructor(private http: HttpClient, 
		public translate: TranslateService,
		private firestore: Firestore,
		public themeService:ThemeService,
		public languageService: LanguageService) {
			console.log('GameService initialization...');
			this._games = <BehaviorSubject<[]>>new BehaviorSubject([]);
			this.gameList = this._games.asObservable();
			this.listGames()
	}

	listGames(){
		this.listenAllGames()
		.pipe(take(1))
		.subscribe(games => {
			this.games = games.sort((a, b) => {
				if(a.name[this.translate.currentLang] && b.name[this.translate.currentLang]){
					if (a.name[this.translate.currentLang].toUpperCase() > b.name[this.translate.currentLang].toUpperCase()) return 1;
					if (a.name[this.translate.currentLang].toUpperCase() < b.name[this.translate.currentLang].toUpperCase()) return -1;
				}
				return 0;
			});
			//this.checkMissingViatorId();
			let totalPlayerCount = 0;
			let totalTeamCount = 0;
			let totalTeamCountInitiated = 0;
			let totalTeamCountInitiatedAndCompleted = 0;
			for(let game of this.games){
				if(game.playerCount>0){
					totalPlayerCount += game.playerCount;
				}
				if(game.teamCount>0){
					totalTeamCount += game.teamCount;
				}
				if(game.teamCountInitiated>0){
					totalTeamCountInitiated += game.teamCountInitiated;
				}
				if(game.teamCountInitiatedAndCompleted>0){
					totalTeamCountInitiatedAndCompleted += game.teamCountInitiatedAndCompleted;
				}
			}
			this.totalPlayerCount = totalPlayerCount;
			this.teamCount = totalTeamCount;
			this.teamCountInitiated = totalTeamCountInitiated;
			this.teamCountInitiatedAndCompleted = totalTeamCountInitiatedAndCompleted;
			this._games.next(this.games);
		});
	}

	checkMissingViatorId(){
		console.log('checkMissingViatorId');
		for(let game of this.games){
			if(game.published && !game.viatorId){
				console.log('Game has no viator id:'+game.name['en']);
			}
		}
	}

	getBackendUrl(){
		return environment.backendUrl;
	}

	getLocalGame(gameId){
		if(this.games){
			return this.games.filter(function( game ) {return game.id == gameId})[0];
		}
		return null;
	}

	getGameByCityId(cityId){
		if(this.games){
			return this.games.filter(function( game ) {return game.cityId == cityId});
		}
		return null;
	}
	listUserGames(userId){
		// Create a GeoCollection reference
		//return this.listenGames(this.geofirestore.collection('games').where('userId', '==', userId));		
		return collectionData(query(collection(this.firestore, 'games'), where('userId', '==', userId)), { idField: 'id' })
		.pipe(map(collection => {
			return collection.map(data => {
				let item = new Game();
				item.fromJSON(data);
				return item;
			});
		}));
	}
	async listFreeeGame(){
		let freeGames = []
		let episodes
		await this.themeService.listAllEpisodes().subscribe(gameEpisodes => {
			if(gameEpisodes){
				episodes = gameEpisodes
				for (const game of this.games) {
					let episode  = episodes.find(episode => episode.id == game.episodeId)
					if(episode && episode?.open){
						freeGames.push(game)
					}
				}
			}
		});
		return freeGames
	}

	listThemeGames(themeId){
		// Create a GeoCollection reference
		return collectionData(query(collection(this.firestore, 'games'), where('themeId', '==', themeId)), { idField: 'id' })
		.pipe(map(collection => {
			return collection.map(data => {
				let item = new Game();
				item.fromJSON(data);
				return item;
			});
		}));
	}

	listenGames(collectionRef){
		return collectionData(query(collection(this.firestore, collectionRef)), { idField: 'id' })
		.pipe(map(collection => {
			return collection.map(data => {
				let item = new Game();
				item.fromJSON(data);
				return item;
			});
		}))
		.pipe(map(games => games.sort((a, b) => {
			if (a.name['fr'] > b.name['fr']) return 1;
			else if (a.name['fr'] < b.name['fr']) return -1;
			else return 0;
		})));
	}

	listTemplateGames(templateId){
		// Create a GeoCollection reference
		return collectionData(query(collection(this.firestore, 'games'), where('templateId', '==', templateId)), { idField: 'id' })
		.pipe(map(collection => {
			return collection.map(data => {
				let item = new Game();
				item.fromJSON(data);
				return item;
			});
		}));	
	}

	listenAllGames(){
		return this.listenGames('games');
	}

	listAllGames(){		
		return collectionData(query(collection(this.firestore, 'games'), orderBy('name.'+this.languageService.currentLang, 'asc')), { idField: 'id' })
		.pipe(map(collection => {
			return collection.map(data => {
				let item = new Game();
				item.fromJSON(data);
				return item;
			});
		}));
	}

	getGame(gameUid){
		console.log('gameService-getGame:'+gameUid);
		return docData(doc(this.firestore, 'games', gameUid))
		.pipe(map(data => {
			let item = new Game();
			item.fromJSON(data);
			return item;
		}));
	}

	getGameById(gameId){
		return docData(doc(this.firestore, 'games', gameId))
		.pipe(map(data => {
			let item = new Game();
			item.fromJSON(data);
			return item;
		}));
	}

	addGame(data) {
		//game.creationDate = firebase.firestore.FieldValue.serverTimestamp()
		//return this.gamesCollection.add({ ...game });
		/*if(data.latitude && data.longitude){
			data.coordinates = new firebase.firestore.GeoPoint(data.latitude, data.longitude);
		}*/
		return addDoc(collection(this.firestore, 'games'), {...data});
	}

	updateGame(gameUid, data){
		/*let gameDoc = this.afs.doc(`games/${gameUid}`);
		console.log('Game uid:'+gameDoc.ref.id);
		return gameDoc.update(data);*/
		//let geodata:any = {d:{ ...data }}
		/*if(data.latitude && data.longitude){
			data.coordinates = new firebase.firestore.GeoPoint(data.latitude, data.longitude);
		}
		if(data.endLatitude && data.endLongitude){
			data.endCoordinates = new firebase.firestore.GeoPoint(data.endLatitude, data.endLongitude);
		}*/
		//console.log('Game to update:'+JSON.stringify(geodata));
		
		return updateDoc(doc(this.firestore, 'games', gameUid), {...data});
	}

	getGameChecks(){
		return collectionData(query(collection(this.firestore, 'game_checks')), { idField: 'id' });
	}

	updateGameCheck(gameUid, data){
		return updateDoc(doc(this.firestore, 'game_checks', gameUid), data);
	}

	setGameCheck(gameUid, data){
		return setDoc(doc(this.firestore, 'game_checks', gameUid), data);
	}

	deleteGameCheck(gameUid) {
		return deleteDoc(doc(this.firestore, 'game_checks', gameUid));
	}

	getLeaderboard(leaderboardUid){
		return docData(doc(this.firestore, 'leaderboard_templates', leaderboardUid))
		.pipe(map(leaderboard => {
			let obj = leaderboard as any;
			return obj;
		}));
	}

	/**
	 * Run setMissionChoicesIds on all games
	 * 
	 * @returns {Promise<void>}
	 */
	async setAllMissionChoicesIds()
	{
		const games: Game[] = await this.listenAllGames()
			.pipe(take(1))
			.toPromise()

		let count = 1;
		for(const game of games)
		{
			console.log('Check game ('+count+'/'+games.length+'):'+game.name['fr']);
			let dataToUpdate = await this.setMissionChoicesIds(game.id);
			if(dataToUpdate){
				console.log('Game was updated. sleep 2sec');
				await new Promise(resolve => setTimeout(resolve, 2000));
			}
			count++;
		}
	}

	listGamePictures(gameUid){
		return collectionData(query(collectionGroup(this.firestore, 'pictures'), where('gameId','==',gameUid)), { idField: 'id' })
		.pipe(map(collection => {
			return collection.map(data => {
				let item = new Picture();
				item.fromJSON(data);
				return item;
			});
		}));
	}

	/**
	 * Assign unique IDs to a game's missions' fork selections and riddle answers
	 * 
	 * @param {string} gameId 
	 * @returns {Promise<any[]>}
	 */
	 async setMissionPictureIds()
	 {
		const pictureQuery = await getDocs(collectionGroup(this.firestore, 'pictures'));
		console.log('Found pictures:'+pictureQuery.docs.length);

		var batch = writeBatch(this.firestore);
		let count = 0;
		let total = 0;

		for(let picDoc of pictureQuery.docs){
			total++;
			//let picDoc = pic.payload.doc;
			//console.log('Found pic:'+picDoc.ref.id);
			let missionRef = picDoc.ref.parent.parent;
			//console.log('Mission id:'+missionRef.id);
			if(missionRef){
				console.log('missionRef:'+missionRef.id);
				let gameRef = missionRef.parent.parent;
				//console.log('Game id:'+gameRef.id);
				let gameTypeRef = gameRef.parent;
				//console.log('Game type:'+gameTypeRef.id);
				//console.log(JSON.stringify(picDoc.data()));
				if((picDoc.data() as any).gameId && (picDoc.data() as any).gameId == gameRef.id){
					console.log('Picture already has a ref to game');
				}
				else if((picDoc.data() as any).templateId && (picDoc.data() as any).templateId == gameRef.id){
					console.log('Picture already has a ref to template');
				}
				else{
					if(gameTypeRef.id == 'games'){
						console.log('Picture gameId should be updated! ');
						batch.update(picDoc.ref, {gameId: gameRef.id});
						//await picDoc.ref.update({gameId: gameRef.id});
						console.log('Picture gameId updated!');
						count++;
					}
					else if(gameTypeRef.id == 'game_templates'){
						console.log('Picture templateId should be updated!');
						batch.update(picDoc.ref, {templateId: gameRef.id});
						//await picDoc.ref.update({templateId: gameRef.id});
						console.log('Picture templateId updated!');
						count++;
					}
				}

				if(count>400){
					// Commit the batch
					console.log('Commit 400 batch');
					await batch.commit();
					count = 0;
					break;
				}
			}
		}
		if(count>0){
			// Commit the batch
			console.log('Commit remaining');
			await batch.commit();
		}
		console.log('---END---'+total);
	 }

	/**
	 * Assign unique IDs to a game's missions' fork selections and riddle answers
	 * 
	 * @param {string} gameId 
	 * @returns {Promise<any[]>}
	 */
	async setMissionChoicesIds(gameId: string)
	{
		const missions = await getDocs(collection(this.firestore, `games/${gameId}/missions`));

		const choiceProps = ['forkSelections', 'riddleAnswers', 'riddleClues']

		const updatePromises = [];
		let dataToUpdate = false;

		missions.docs.forEach(async missionDoc => {
			let missionData: any = missionDoc.data()
			let updateData: any = {}

			choiceProps.forEach(choiceProp => {
				if(missionData[choiceProp]){
					let requiresUpdate = missionData[choiceProp].some(choice => choice.uid == null)
	
					if(requiresUpdate)
					{
						dataToUpdate = true;
						console.log('Mission '+missionData.name['fr']+' requires update for '+choiceProp);
						//console.log('Before: '+JSON.stringify(missionData[choiceProp]));
						updateData[choiceProp] = missionData[choiceProp];
						updateData[choiceProp]
						.map(choice => {
							///console.log('choice:'+JSON.stringify(choice));
							if(choice.uid == null){
								choice.uid = uuid();
							}
						});
						//console.log('After: '+JSON.stringify(updateData[choiceProp]));
					}
				}
			});

			if(Object.keys(updateData).length > 0){
				console.log('Update mission:'+JSON.stringify(updateData));
				await updateDoc(missionDoc.ref, updateData);
			}
			//if(Object.keys(updateData).length > 0) updatePromises.push(missionDoc.ref.update(missionData))
		})
		return dataToUpdate;
		//return Promise.all(updatePromises)
	}

	async fixGameMissionIds()
	{
		const missionQuery = await getDocs(query(collectionGroup(this.firestore, 'missions'), where('scope','==','game')));
		
		console.log('Found missions:'+missionQuery.docs.length);

		var batch = writeBatch(this.firestore);
		let count = 0;
		let total = 0;

		for(let missionDoc of missionQuery.docs){
			//let picDoc = pic.payload.doc;
			//console.log('Found pic:'+picDoc.ref.id);
			let missionRef = missionDoc.ref;
			//console.log('Mission id:'+missionRef.id);
			if(missionRef){
				//console.log('missionRef:'+missionRef.id);
				if((missionDoc.data() as any).id !== missionRef.id){
					console.log('We should update mission:'+(missionDoc.data() as any).id+' to '+missionRef.id);
					count++;
					batch.update(missionRef, {id: missionRef.id});
				}

				if(count>400){
					// Commit the batch
					total += count;
					console.log('Commit 400 batch');
					await batch.commit();
					batch = writeBatch(this.firestore);
					count = 0;
					//break;
				}
			}
		}
		if(count>0){
			// Commit the batch
			console.log('Commit remaining');
			total += count;
			await batch.commit();
		}
		console.log('---END--- '+total+' docs to update');
	}

	copyGame(gameUid, newName, desc, asTemplate){
		let url = environment.backendUrl+'copyGame?gameId='+gameUid+'&destName='+newName+'&desc='+desc;
		if(asTemplate){
			url += '&asTemplate=true';
		}
		return this.http.get(url);
	}

	deployGame(templateUid, targetGameId){
		let url = environment.backendUrl+'copyGame?deploy=true&gameId='+templateUid+'&targetGameId='+targetGameId;
		return this.http.get(url);
	}

	refreshStats(gameId)
	{
		const url = environment.backendUrl + 'refreshGameData?gameId=' + gameId
		return this.http.get(url)
	}

	async refreshAllStats(){
		for(let game of this.games){
			console.log('Refresh stats for game '+game.id);
			let res = await this.refreshStats(game.id).pipe(first()).toPromise();
			console.log('RES:'+res);
			await new Promise(resolve => setTimeout(resolve, 2000))
		}
	}

	deleteGame(gameUid) {
		let gameDoc = doc(this.firestore, 'games', gameUid);
		return deleteDoc(gameDoc);
	}

	/*//Save first document in snapshot of items received
	firstInResponse: any = [];
	//Save last document in snapshot of items received
	lastInResponse: any = [];*/

	listGameTeamPictures(videosOnly, gameUid, limit, lastInResponse){
		let collectionRef = collection(this.firestore, `games/${gameUid}/teampictures`);
		let colQuery = query(collectionRef, orderBy('time', 'desc'), limit(limit));
		if(videosOnly){
			colQuery = query(collectionRef, where('type','==','video'), orderBy('time', 'desc'), limit(limit));
		}
		return collectionData(colQuery)
		.pipe(map(changes => {
			//this.firstInResponse = changes[0].payload.doc;
			if(changes.length>0){
				lastInResponse = changes[changes.length - 1].payload.doc;
				return {
					data: changes.map(a => {
						//console.log('received mission:'+JSON.stringify(a.payload.doc.data()));
						let data:any = a.payload.doc.data() as Picture;
						data.id = a.payload.doc.id;
						return data;
					}),
					lastInResponse: lastInResponse
				};
			}else{
				return {lastInResponse: [], data: []}
			}
		}));
	}

	listMoreGameTeamPictures(gameUid, videosOnly, limit, lastInResponse){
		let collectionRef = collection(this.firestore, `games/${gameUid}/teampictures`);
		
		let colQuery = query(collectionRef, orderBy('time', 'desc'), startAfter(lastInResponse), limit(limit));
		if(videosOnly){
			colQuery = query(collectionRef, where('type','==','video'), orderBy('time', 'desc'), startAfter(lastInResponse), limit(limit));
		}
		return collectionData(colQuery)
		.pipe(map(changes => {
			//this.firstInResponse = changes[0].payload.doc;
			lastInResponse = changes[changes.length - 1].payload.doc;
			return {
				data: changes.map(a => {
					//console.log('received mission:'+JSON.stringify(a.payload.doc.data()));
					let data:any = a.payload.doc.data();
					data.id = a.payload.doc.id;
					return data;
				}),
				lastInResponse: lastInResponse
			};
		}));
	}

	listAllPictures(videosOnly, limit, lastInResponse){
		// Get all the user's comments, no matter how deeply nested
		let collectionRef = collectionGroup(this.firestore, `teampictures`);
		
		let colQuery = query(collectionRef, orderBy('time', 'desc'), startAfter(lastInResponse), limit(limit));
		if(videosOnly){
			colQuery = query(collectionRef, where('type','==','video'), orderBy('time', 'desc'), startAfter(lastInResponse), limit(limit));
		}
		return collectionData(colQuery)
		.pipe(map(changes => {
			//this.firstInResponse = changes[0].payload.doc;
			if(changes.length>0){
				lastInResponse = changes[changes.length - 1].payload.doc;
				console.log('gameService-listAllPictures, lastInResponse:'+lastInResponse);
				return {
					data: changes.map(a => {
						//console.log('received mission:'+JSON.stringify(a.payload.doc.data()));
						let data:any = a.payload.doc.data();
						data.id = a.payload.doc.id;
						return data;
					}),
					lastInResponse: lastInResponse
				};
			}else{
				return {lastInResponse: [], data: []}
			}
		}));
	}
	
	listPicturesTeam(teamId){
		let collectionRef = collectionGroup(this.firestore, `teampictures`);
		let colQuery = query(collectionRef, where('teamId', '==', teamId));
		return collectionData(colQuery)
		.pipe(map(collection => {
			return collection.map(data => {
				let item = new Picture();
				item.fromJSON(data);
				return item;
			});
		}));
	}

	listMoreTeamPictures(videosOnly, limit, lastInResponse){
		let collectionRef = collectionGroup(this.firestore, `teampictures`);
		let colQuery = query(collectionRef, orderBy('time', 'desc'), startAfter(lastInResponse), limit(limit));
		if(videosOnly){
			colQuery = query(collectionRef, where('type','==','video'), orderBy('time', 'desc'), startAfter(lastInResponse), limit(limit));
		}
		return collectionData(colQuery)
		.pipe(map(changes => {
			//this.firstInResponse = changes[0].payload.doc;
			lastInResponse = changes[changes.length - 1].payload.doc;
			return {
				data: changes.map(collection => {
					return collection.map(data => {
						let item = new Picture();
						item.fromJSON(data);
						return item;
					});
				}),
				lastInResponse: lastInResponse
			};
		}));
	}

	deletePicture(gameUid, teamPictureId) {
		let gamePicDoc = doc(this.firestore, `games/${gameUid}/teampictures/${teamPictureId}`);
		return deleteDoc(gamePicDoc);
	}

	getDiagram(gameUid){
		return docData(doc(this.firestore, `games/${gameUid}/diagrams/default`))
		.pipe(map(diagram => {
			let obj = diagram as any;
			return obj;
		}));
	}

	setDiagram(gameId, diagram) {
		//game.creationDate = firebase.firestore.FieldValue.serverTimestamp()
		return setDoc(doc(this.firestore, `games/${gameId}/diagrams/default`), diagram);
	}

	listDirections(gameUid) {
		return collectionData(collection(this.firestore, `games/${gameUid}/directions`), { idField: 'id' });
	}

	addDirections(gameUid, data){
		//console.log('Create mission image:'+JSON.stringify(data));
		return addDoc(collection(this.firestore, `games/${gameUid}/directions`), {...data});
	}

	updateDirections(gameUid, directionsUid, data){
		return updateDoc(doc(this.firestore, `games/${gameUid}/directions/${directionsUid}`), data);
	}

	createMailing(gameUid, mailing){
		return addDoc(collection(this.firestore, `games/${gameUid}/mailings`), {...mailing});
	}

	uploadDropbox(gameUid, teamId){
		console.log('Call url: '+environment.backendUrl+'exportSlideshow?gameUid='+gameUid+'&teamUid='+teamId);
		return this.http.get(environment.backendUrl+'exportSlideshow?gameUid='+gameUid+'&teamUid='+teamId);
	}

	prepareExportGoogleDrive(gameUid){
		console.log('Call url: '+environment.backendUrl+'prepareExportGoogleDrive?gameUid='+gameUid);
		return this.http.get(environment.backendUrl+'prepareExportGoogleDrive?gameUid='+gameUid);
	}

	uploadGoogleDrive(gameUid, teamId){
		console.log('Call url: '+environment.backendUrl+'exportGoogleDrive?gameUid='+gameUid+'&teamUid='+teamId);
		return this.http.get(environment.backendUrl+'exportGoogleDrive?gameUid='+gameUid+'&teamUid='+teamId);
	}

	updateObjectPicturesUrl(gameUid){
		console.log('Call url: '+environment.backendUrl+'updateObjectPicturesUrl?gameId='+gameUid);
		return this.http.get(environment.backendUrl+'updateObjectPicturesUrl?gameId='+gameUid);
	}
}